import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import OpenDentalBookingModal from '../components/OpenDentalBookingModal';

// ---------------------------------------------------------------------------
// OpenDental booking interception.
//
// When a lead is dragged / moved into a "Won" stage, callers (PipelineBoard,
// LeadDetail, the drawer) await `interceptWonTransition({ lead, targetStage })`
// BEFORE committing the stage change. If this org has OpenDental enabled +
// configured and the lead isn't already synced, we first show a lightweight
// prompt ("schedule an appointment?"). Booking is OPTIONAL:
//   Schedule appointment → the existing booking modal (booked = proceed,
//                          closed = cancelled — strict flow kept on purpose)
//   Skip for now         → proceed, Won lands with no appointment
//   dismiss (X/backdrop) → cancelled; deliberate skipping needs the explicit
//                          button so stray ESC never creates an unbooked Won
// Otherwise we resolve 'proceed' immediately so non-dental orgs never see
// anything. Design: docs/plans/2026-07-16-opendental-optional-booking-design.md
//
// One modal at a time: a single shared modal instance lives here, driven by a
// ref-held promise resolver.
// ---------------------------------------------------------------------------
const OpenDentalBookingContext = createContext(null);

export const useOpenDentalBooking = () => {
  const ctx = useContext(OpenDentalBookingContext);
  if (!ctx) {
    throw new Error('useOpenDentalBooking must be used within an OpenDentalBookingProvider');
  }
  return ctx;
};

export const OpenDentalBookingProvider = ({ children }) => {
  const { organization } = useAuth();

  // phase: 'prompt' (schedule-or-skip dialog) | 'booking' (the full modal)
  const [modalState, setModalState] = useState({ open: false, phase: 'prompt', lead: null, targetStage: null });
  // Holds the resolver for the in-flight intercept promise so the modal's
  // callbacks can settle it. Cleared after each resolution.
  const resolverRef = useRef(null);

  const settle = useCallback((outcome) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setModalState({ open: false, phase: 'prompt', lead: null, targetStage: null });
    if (resolve) resolve(outcome);
  }, []);

  // Org has the add-on on AND at least one configured, non-archived
  // branch. (Legacy single-config orgs are covered too once migrated into
  // locations[]; the modal's own branch picker handles 1 vs many.)
  const isOpenDentalReady =
    organization?.opendental?.enabled === true &&
    (organization?.opendental?.locations || []).some(
      (l) => l && l.isConfigured && !l.archived
    );

  // Manual entry point — "Schedule OpenDental appointment" on Lead Detail
  // (e.g. after the operator chose "Skip for now" on a Won transition).
  // No pending promise: nothing awaits this, the modal just opens.
  const openBookingForLead = useCallback((lead) => {
    if (resolverRef.current) {
      const prev = resolverRef.current;
      resolverRef.current = null;
      prev('cancelled');
    }
    setModalState({ open: true, phase: 'booking', lead, targetStage: null });
  }, []);

  const interceptWonTransition = useCallback(
    ({ lead, targetStage }) => {
      const odEnabled = isOpenDentalReady;
      const isWon = targetStage?.closedType === 'won';
      const alreadySynced = lead?.opendental?.syncStatus === 'synced';

      // Not a dental Won transition (or already synced) -> proceed silently.
      if (!isWon || !odEnabled || alreadySynced) {
        return Promise.resolve('proceed');
      }

      // Only one modal at a time — if one is already pending, cancel the old
      // intercept so we don't strand its promise.
      if (resolverRef.current) {
        const prev = resolverRef.current;
        resolverRef.current = null;
        prev('cancelled');
      }

      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setModalState({ open: true, phase: 'prompt', lead, targetStage });
      });
    },
    [isOpenDentalReady]
  );

  const leadName = [modalState.lead?.firstName, modalState.lead?.lastName]
    .filter(Boolean).join(' ') || 'this lead';

  return (
    <OpenDentalBookingContext.Provider value={{ interceptWonTransition, openBookingForLead, isOpenDentalReady }}>
      {children}

      {/* Phase 1 — optional-booking prompt. Backdrop/X cancels the move;
          only the explicit Skip button proceeds without an appointment. */}
      {modalState.open && modalState.phase === 'prompt' && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => settle('cancelled')}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">🎉 Lead won!</h3>
            <p className="mt-2 text-sm text-gray-600">
              Would you like to schedule an OpenDental appointment for {leadName}?
              You can also do this later from the lead's page.
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => settle('proceed')}
                className="btn-secondary"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={() => setModalState((s) => ({ ...s, phase: 'booking' }))}
                className="btn-primary"
              >
                Schedule appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2 — the existing booking modal, behavior unchanged. */}
      <OpenDentalBookingModal
        open={modalState.open && modalState.phase === 'booking'}
        lead={modalState.lead}
        targetStage={modalState.targetStage}
        onBooked={() => settle('proceed')}
        onClose={() => settle('cancelled')}
      />
    </OpenDentalBookingContext.Provider>
  );
};

export default OpenDentalBookingProvider;
